import { getPagesApi } from '@/api-calls/posts/get'
import { useAuth } from '@/auth/auth-context'
import { ContainerWithSidebar } from '@/components/header/container'
import { useSite } from '@/hooks/use-site'
import { Button, Skeleton, ToggleGroup, ToggleGroupItem } from '@blogging/ui'
import { useFetch, withQueryClientProvider } from '@ngtrvu/use-api'
import { formatDistance } from 'date-fns'
import { ArrowRight, Lock, Pencil } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

type StatusFilter = 'all' | 'published' | 'draft'

const STATUS_FILTERS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Published', value: 'published' },
  { label: 'Drafts', value: 'draft' },
]

function parseStatusFilter(value: string | null): StatusFilter {
  return STATUS_FILTERS.some((f) => f.value === value)
    ? (value as StatusFilter)
    : 'all'
}

function getStatusColor(status: string) {
  switch (status) {
    case 'draft':
      return 'text-gray-500'
    case 'published':
      return 'text-green-500'
  }
}

function SitePagesPageContent() {
  const { siteId } = useParams()
  const { isLoading: isSiteLoading, canManageAllContent } = useSite()
  const { user } = useAuth()

  const [searchParams, setSearchParams] = useSearchParams()
  const statusFilter = parseStatusFilter(searchParams.get('status'))

  const setStatusFilter = (value: StatusFilter) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value === 'all') {
          next.delete('status')
        } else {
          next.set('status', value)
        }
        return next
      },
      { replace: true },
    )
  }

  const { data: pages, isLoading } = useFetch(getPagesApi, {
    resourceName: 'pages',
    initialParams: {
      siteId: siteId as string,
    },
  })

  const isLoadingData = isLoading || isSiteLoading

  const filteredPages = useMemo(() => {
    if (!pages) return []
    if (statusFilter === 'all') return pages
    return pages.filter((page: any) => page.status === statusFilter)
  }, [pages, statusFilter])

  // Check if user can edit a specific page
  const canEditPage = (page: any) => {
    if (canManageAllContent) return true // Admin or Editor can edit all
    return page.createdById === user?.id // Author can only edit their own
  }

  return (
    <ContainerWithSidebar>
      <header className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Pages</h1>
        <div className="flex items-center gap-2">
          {!isLoadingData && pages && pages.length > 0 && (
            <ToggleGroup
              type="single"
              variant="outline"
              value={statusFilter}
              onValueChange={(value) =>
                value && setStatusFilter(value as StatusFilter)
              }
            >
              {STATUS_FILTERS.map((filter) => (
                <ToggleGroupItem key={filter.value} value={filter.value}>
                  {filter.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          )}
          <Link to={`/${siteId}/editor/page/new`}>
            <Button variant="default">New page</Button>
          </Link>
        </div>
      </header>

      <main className="mt-6">
        {isLoadingData ? (
          <div className="space-y-3">
            <Skeleton className="h-[72px] w-full rounded-lg" />
          </div>
        ) : !pages || pages.length === 0 ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 my-8">
              Your blog is empty, create your first page to get started.
            </p>
            <Link to={`/${siteId}/editor/page/new`}>
              <Button variant="default">
                Create your first page <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        ) : filteredPages.length === 0 ? (
          <p className="text-sm text-gray-500 my-8">
            No {statusFilter === 'all' ? '' : statusFilter} pages found.
          </p>
        ) : (
          <div className="space-y-3">
            {filteredPages.map((page: any) => {
              const { createdBy: author } = page
              const canEdit = canEditPage(page)

              return canEdit ? (
                <Link
                  key={page.id}
                  to={`/${siteId}/editor/page/${page.id}`}
                  className="flex items-center justify-between rounded-lg border p-4 hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <div className="space-y-1">
                    <h3 className="font-medium">{page.title}</h3>
                    <p className="text-sm text-gray-500">
                      By {author?.name} -{' '}
                      {formatDistance(page.createdAt, new Date(), {
                        addSuffix: true,
                      })}
                    </p>
                    <span className={`text-sm font-medium ${getStatusColor(page.status)}`}>
                      {page.status}
                    </span>
                  </div>
                  <div className="h-9 w-9 flex items-center justify-center">
                    <Pencil className="h-4 w-4" />
                  </div>
                </Link>
              ) : (
                <div
                  key={page.id}
                  className="flex items-center justify-between rounded-lg border p-4 bg-muted/30 cursor-not-allowed opacity-60"
                >
                  <div className="space-y-1">
                    <h3 className="font-medium">{page.title}</h3>
                    <p className="text-sm text-gray-500">
                      By {author?.name} -{' '}
                      {formatDistance(page.createdAt, new Date(), {
                        addSuffix: true,
                      })}
                    </p>
                    <span className={`text-sm font-medium ${getStatusColor(page.status)}`}>
                      {page.status}
                    </span>
                  </div>
                  <div className="h-9 w-9 flex items-center justify-center">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </main>
    </ContainerWithSidebar>
  )
}

export const SitePagesPage = withQueryClientProvider(SitePagesPageContent)
